import React from 'react';
import styled from '@emotion/styled';
import Modal, { ModalTransition, ModalHeader, ModalTitle, ModalBody, ModalFooter } from '@atlaskit/modal-dialog';
import { Text } from '@atlaskit/primitives';
import Button from '@atlaskit/button/new';
import CrossIcon from '@atlaskit/icon/glyph/cross';
import { token } from '@atlaskit/tokens';
import { N500 } from '@atlaskit/theme/colors';
import { useCloudSiteOrOrgNameMapStore, useStore } from '../../../../store';
import { SelectType } from '../../../billing-profile/src/types';
import { UpdateExistingEntitlementWrapper } from '../../../billing-profile/src/update-existing-entitlement-wrapper';

export const ModalHeaderWrapper = styled.div`
  width: 100%;
`;

export const ModalTitleWrapper = styled.div`
  width: 100%;
`;
interface Props {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (checkedItemMap: Record<string, SelectType>) => void;
  customer?: { name: string; ids: string[] } | null;
  invoiceGroupId?: string;
  isBillingProfileDetailsLoading?: boolean;
  billingProfileError?: any;
  refetchBillingProfileDetails?: any;
  fetchEntitlementListError?: any;
  isFetchEntitlementListLoading?: boolean;
  refetchEntitlementsResponse?: any;
  externalProductsList?: any;
  isExternalProductListLoading?: boolean;
  isTWCPhase2Enabled?: boolean;
}

export const UpdateExistingEntitlementModal = ({
  isOpen,
  customer,
  invoiceGroupId,
  onClose,
  onSuccess,
  isBillingProfileDetailsLoading = false,
  billingProfileError,
  refetchBillingProfileDetails,
  fetchEntitlementListError,
  isFetchEntitlementListLoading = false,
  refetchEntitlementsResponse,
  externalProductsList,
  isExternalProductListLoading = false,
  isTWCPhase2Enabled = false,
}: Props) => {
  const [checkedItemMap, setCheckedItemMap] = React.useState<Record<string, SelectType>>({});
  const [alreadyCheckedItemMap, setAlreadyCheckedItemMap] = React.useState<Record<string, SelectType>>({});
  const [cloudSiteOrOrgNameMapData, actions] = useCloudSiteOrOrgNameMapStore();
  const [{ selectedProducts }] = useStore();

  React.useEffect(() => {
    const newAlreadyCheckedItemMap: Record<string, SelectType> = {};
    Object.keys(selectedProducts).forEach((key: string) => {
      newAlreadyCheckedItemMap[key] = SelectType.ENTITLEMENT;
    });
    setAlreadyCheckedItemMap(newAlreadyCheckedItemMap);
  }, [selectedProducts]);

  return (
    <ModalTransition>
      {isOpen && (
        <Modal
          onClose={onClose}
          testId="update-existing-entitlement-modal"
          shouldScrollInViewport
          height={690}
          width={752}
        >
          <ModalHeader>
            <ModalHeaderWrapper>
              <ModalTitleWrapper className="flex align-center justify-space-between">
                <ModalTitle>Select subscriptions you want to update</ModalTitle>
                <Button
                  appearance="subtle"
                  onClick={() => {
                    onClose();
                    setCheckedItemMap({});
                    actions.setSearchQuery('');
                  }}
                >
                  <CrossIcon size="small" label="Close Modal" primaryColor={token('color.text.subtle', N500)} />
                </Button>
              </ModalTitleWrapper>
              <Text size="small" color="color.text.subtlest">
                Add individual subscriptions or entire sites to your order.
              </Text>
            </ModalHeaderWrapper>
          </ModalHeader>
          <ModalBody>
            {customer && invoiceGroupId && (
              <UpdateExistingEntitlementWrapper
                customerIds={customer.ids}
                checkedItemMap={checkedItemMap}
                setCheckedItemMap={setCheckedItemMap}
                alreadyCheckedItemMap={alreadyCheckedItemMap}
                billingProfileError={billingProfileError}
                refetchBillingProfileDetails={refetchBillingProfileDetails}
                isBillingProfileDetailsLoading={isBillingProfileDetailsLoading}
                fetchEntitlementListError={fetchEntitlementListError}
                refetchEntitlementsResponse={refetchEntitlementsResponse}
                isFetchEntitlementListLoading={isFetchEntitlementListLoading}
                externalProductsList={externalProductsList}
                isExternalProductListLoading={isExternalProductListLoading}
                cloudSiteOrOrgNameMapData={cloudSiteOrOrgNameMapData}
                isTWCPhase2Enabled={isTWCPhase2Enabled}
              />
            )}
          </ModalBody>
          <ModalFooter>
            <Button
              appearance="subtle"
              onClick={() => {
                onClose();
                setCheckedItemMap({});
                actions.setSearchQuery('');
              }}
            >
              Cancel
            </Button>
            <Button
              appearance="primary"
              isDisabled={Object.keys(checkedItemMap).length === 0}
              onClick={() => {
                onSuccess(checkedItemMap);
                setCheckedItemMap({});
              }}
            >
              Add to order
            </Button>
          </ModalFooter>
        </Modal>
      )}
    </ModalTransition>
  );
};
